import React, { useCallback, useRef, useEffect } from "react";
import { useAtom } from "jotai";
import { motion } from "framer-motion";
import { themeAtom, isMobileAtom } from "../../../../../-Atoms";
import { db, doc, updateDoc } from "../../../../../firebaseConfig";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import debounce from "lodash.debounce";

import UniversalTextField from "../../../../../GlobalComponents/UniversalTextField";

const StaffV2 = ({
  additionalOperators,
  staffData,
  findStaff,
  operator,
  setOperator,
  jobData,
  retoucherError,
  jobTypeFull,
  retoucher,
  setRetoucher,
  retouchingTimeError,
  retouchingTime,
  handleValueChange,
  setRetouchingTime,
}) => {
  const [theme] = useAtom(themeAtom);
  const [isMobile] = useAtom(isMobileAtom);

  const jobDataIdRef = useRef(jobData.id);
  useEffect(() => {
    jobDataIdRef.current = jobData.id;
  }, [jobData.id]);

  // eslint-disable-next-line
  const updateFirestore = useCallback(
    debounce((field, value) => {
      updateDoc(doc(db, "JobData", jobDataIdRef.current), { [field]: value }).catch((e) =>
        console.log(e)
      );
    }, 500),
    []
  );

  const handleOperatorChange = (e, value) => {
    const val = value?.id || "";
    setOperator(val);
    updateFirestore("operator", val);
  };

  const handleRetoucherChange = (e, value) => {
    const val = value?.id || "";
    setRetoucher(val);
    updateFirestore("retoucher", val);
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        ease: [0.25, 0.1, 0.25, 1.0],
      },
    },
  };

  return (
    <motion.div variants={itemVariants}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          borderRadius: "10px",
          backgroundColor:
            theme === "dark" ? "rgba(35,35,35,0.7)" : "rgba(235,235,235,0.9)",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          boxShadow:
            theme === "dark"
              ? "0 2px 10px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.1)"
              : "0 2px 10px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)",
          padding: 2,
          marginBottom: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Operators Row */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <Autocomplete
              disablePortal
              autoHighlight
              openOnFocus
              sx={{
                flex: additionalOperators.length > 0 ? "1 1 48%" : "1 1 100%",
                minWidth: "200px",
              }}
              options={[
                ...staffData.filter(
                  (staff) => staff.currentlyEmployed || staff.id === operator
                ),
              ]}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              getOptionDisabled={(option) => !option.currentlyEmployed}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="filled"
                  size="small"
                  label="Operator"
                  inputProps={{ ...params.inputProps, style: { fontSize: 15 } }}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    style: { fontSize: 15 },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                      },
                      "&.Mui-focused": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(60,60,60,0.9)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 0 0 2px rgba(100,100,255,0.5)"
                            : "0 0 0 2px rgba(100,100,255,0.3)",
                      },
                    },
                  }}
                />
              )}
              value={operator ? staffData.find((s) => s.id === operator) : null}
              onChange={handleOperatorChange}
            />

            {additionalOperators &&
              additionalOperators.map((ao, index) => (
                <TextField
                  sx={{
                    flex: isMobile ? "1 1 45%" : "1 1 48%",
                    minWidth: "200px",
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                      },
                      "&.Mui-focused": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(60,60,60,0.9)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 0 0 2px rgba(100,100,255,0.5)"
                            : "0 0 0 2px rgba(100,100,255,0.3)",
                      },
                    },
                  }}
                  key={`${ao}_${index}`}
                  variant="filled"
                  label="Additional Operator"
                  value={findStaff(ao, staffData) || ""}
                  size="small"
                  inputProps={{ style: { fontSize: 15 } }}
                  InputLabelProps={{ style: { fontSize: 15 } }}
                  InputProps={{ disableUnderline: true }}
                />
              ))}
          </Box>

          {/* Retoucher/Editor Row */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "flex-start",
            }}
          >
            <Autocomplete
              disablePortal
              autoHighlight
              openOnFocus
              sx={{ flex: "1 1 48%", minWidth: "200px" }}
              options={[
                ...staffData.filter(
                  (staff) => staff.currentlyEmployed || staff.id === retoucher
                ),
              ]}
              getOptionLabel={(option) =>
                `${option.firstName} ${option.lastName}`
              }
              getOptionDisabled={(option) => !option.currentlyEmployed}
              isOptionEqualToValue={(option, value) => option?.id === value?.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={retoucherError}
                  variant="filled"
                  fullWidth
                  inputProps={{ ...params.inputProps, style: { fontSize: 15 } }}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    style: { fontSize: 15 },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    disableUnderline: true,
                  }}
                  size="small"
                  label={
                    jobTypeFull?.includes("(-V)") ||
                    jobTypeFull?.includes("(-E)")
                      ? "Editor *"
                      : "Retoucher *"
                  }
                  sx={{
                    "& .MuiFilledInput-root": {
                      borderRadius: "8px",
                      backgroundColor:
                        theme === "dark"
                          ? "rgba(45,45,45,0.7)"
                          : "rgba(250,250,250,0.9)",
                      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                      "&:hover": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(55,55,55,0.8)"
                            : "rgba(255,255,255,1)",
                      },
                      "&.Mui-focused": {
                        backgroundColor:
                          theme === "dark"
                            ? "rgba(60,60,60,0.9)"
                            : "rgba(255,255,255,1)",
                        boxShadow:
                          theme === "dark"
                            ? "0 0 0 2px rgba(100,100,255,0.5)"
                            : "0 0 0 2px rgba(100,100,255,0.3)",
                      },
                    },
                  }}
                />
              )}
              value={
                retoucher ? staffData.find((s) => s.id === retoucher) : null
              }
              onChange={handleRetoucherChange}
            />

            <Box sx={{ flex: "1 1 48%", minWidth: "200px" }}>
              <UniversalTextField
                collectionName="JobData"
                docId={jobData.id}
                fieldName="retouchingTime"
                value={retouchingTime || ""}
                label={
                  jobTypeFull?.includes("(-V)") || jobTypeFull?.includes("(-E)")
                    ? "Editing Time *"
                    : "Retouching Time"
                }
                variant="filled"
                multiline
                error={retouchingTimeError}
                inputProps={{ style: { fontSize: 15 } }}
                InputLabelProps={{ style: { fontSize: 15 } }}
                noPadding
                size="small"
                sx={{
                  marginTop: 0,
                  "& .MuiInputBase-root": {
                    fontSize: 15,
                  },
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>
    </motion.div>
  );
};

export default StaffV2;
